import type { TargetAndTransition, Transition, Variants } from 'framer-motion';

type CardTargetResolver = Exclude<Variants[string], TargetAndTransition>;

export interface FlowThemeCardMotion {
  variants: Variants;
  transition: Transition;
  /** Inline style for the moving card wrapper (e.g. perspective for 3D swings). */
  style?: Record<string, number | string>;
}

/** A theme's card transition for the current layout. */
export type FlowThemeCardMotionFactory = (isMobile: boolean) => FlowThemeCardMotion;

/** 1 when the learner moves forward, -1 going back. */
export type CardDirection = 1 | -1;

/** A blur filter on desktop only: phones skip filters to keep transitions smooth. */
export const desktopBlur = (isMobile: boolean, amount: number) => (isMobile ? undefined : `blur(${amount}px)`);

/**
 * The card drifts up and away like light or a balloon rising into the sky, and
 * the next one rises gently in from below.
 */
export const risingCardMotion = (isMobile: boolean, rise: number, blurAmount: number): FlowThemeCardMotion => ({
  variants: {
    enter: (direction: CardDirection) => ({ opacity: 0, y: direction > 0 ? rise : -rise, filter: desktopBlur(isMobile, blurAmount) }),
    center: { opacity: 1, y: 0, filter: isMobile ? undefined : 'blur(0px)' },
    exit: (direction: CardDirection) => ({
      opacity: 0,
      y: direction > 0 ? -rise * 1.2 : rise * 1.2,
      filter: desktopBlur(isMobile, blurAmount),
    }),
  },
  transition: { duration: 1, ease: [0.25, 1, 0.5, 1] },
});

/**
 * A settled card must not keep `filter: blur(0px)`: any filter on the wrapper
 * becomes the backdrop root for the card's frosted `backdrop-filter` (so the
 * glass stops blurring the scene in Chromium) and costs extra repaints. Drop
 * it once the card has arrived; the exit blur animates from 'none' as normal.
 */
const clearSettledFilter = (motion: FlowThemeCardMotion): FlowThemeCardMotion => {
  const center = motion.variants.center;
  if (!center) {
    return motion;
  }
  // An omitted filter keeps the previous motion value when a mounted card
  // switches from desktop to mobile. Explicitly clear that desktop blur.
  const normalize = (target: TargetAndTransition): TargetAndTransition => ({
    ...target, filter: target.filter ?? 'none', transitionEnd: { ...target.transitionEnd, filter: 'none' },
  });
  const normalizedCenter: TargetAndTransition | CardTargetResolver = typeof center === 'function'
    ? (...args) => {
      const target = center(...args);
      return typeof target === 'string' ? target : normalize(target);
    }
    : normalize(center);
  return {
    ...motion,
    variants: {
      ...motion.variants,
      center: normalizedCenter,
    },
  };
};

/**
 * Builds a theme's card transition, or returns null so the player keeps its
 * default horizontal slide (the fallback for themes without a card motion).
 */
export const buildFlowThemeCardMotion = (
  factory: FlowThemeCardMotionFactory | undefined,
  isMobile: boolean,
): FlowThemeCardMotion | null => (factory ? clearSettledFilter(factory(isMobile)) : null);
